import { parseISO } from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import {
  PageModuleDataWithPageId,
  PageModuleStatus,
} from "entities/pages/types";
import { Module } from "./helpers";

interface MonthData {
  year: number;
  month: number;
  date: Date;
  modules: Module[];
}

interface Group {
  title: string;
  data: Module[];
  initiallyExpanded: boolean;
  count?: number;
}

export const groupModulesByMonth = (
  modules: PageModuleDataWithPageId[],
): Group[] => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const groups: Group[] = [];

  const addedGroupNames = new Set<string>();

  const pastToCurrentModulesByMonth: Record<string, Module[]> = {};
  const modulesByMonth: Record<string, MonthData> = {};

  const parseDate = (dateString?: string): Date | null => {
    if (!dateString) return null;
    const [datePart, timePart, timezone = "UTC"] = dateString.split(" ");

    const isoString = timePart
      ? `${datePart}T${timePart}:00`
      : `${datePart}T00:00:00`;
    const localDate = parseISO(isoString);

    const tzOffset = getTimezoneOffset(timezone, localDate);

    return new Date(localDate.getTime() - tzOffset);
  };

  const getMonthName = (date: Date): string => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(date);
  };

  const isCurrentMonth = (date: Date): boolean => {
    return (
      date.getFullYear() === currentYear && date.getMonth() === currentMonth
    );
  };

  const addGroup = (
    title: string,
    data: Module[],
    initiallyExpanded: boolean = false,
  ): void => {
    const sortedData = [...data].sort((a, b) => {
      if (!a.scheduledStartAt) return 1;
      if (!b.scheduledStartAt) return -1;

      const dateA = parseDate(a.scheduledStartAt);
      const dateB = parseDate(b.scheduledStartAt);

      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateA.getTime() - dateB.getTime();
    });

    if (addedGroupNames.has(title)) {
      const existingGroup = groups.find((g) => g.title === title);
      if (existingGroup) {
        existingGroup.data = [...existingGroup.data, ...sortedData].sort(
          (a, b) => {
            if (!a.scheduledStartAt) return 1;
            if (!b.scheduledStartAt) return -1;

            const dateA = parseDate(a.scheduledStartAt);
            const dateB = parseDate(b.scheduledStartAt);

            if (!dateA) return 1;
            if (!dateB) return -1;

            return dateA.getTime() - dateB.getTime();
          },
        );
      }
    } else {
      groups.push({
        title,
        data: sortedData,
        initiallyExpanded,
        count: sortedData.length,
      });
      addedGroupNames.add(title);
    }
  };

  const unscheduledModules = modules.filter((module) => {
    if (!module.scheduledStartAt) return true;

    const endDate = parseDate(module.scheduledEndAt);

    if (endDate && endDate < now && module.status === PageModuleStatus.Active) {
      return true;
    }

    return false;
  });

  if (unscheduledModules.length > 0) {
    const cleanedUnscheduledModules = unscheduledModules.map((module) => ({
      ...module,
      scheduledStartAt: undefined,
      scheduledEndAt: undefined,
    }));

    addGroup("Unscheduled", cleanedUnscheduledModules);
  }

  modules.forEach((module) => {
    if (!module.scheduledStartAt) return;

    const startDate = parseDate(module.scheduledStartAt);
    const endDate = parseDate(module.scheduledEndAt);

    if (!startDate) return;

    if (endDate && endDate < now && module.status === PageModuleStatus.Active)
      return;

    if (
      endDate &&
      startDate < now &&
      startDate.getMonth() < currentMonth &&
      isCurrentMonth(endDate) &&
      endDate > now &&
      module.status === PageModuleStatus.Active
    ) {
      const monthName = getMonthName(startDate);
      if (!pastToCurrentModulesByMonth[monthName]) {
        pastToCurrentModulesByMonth[monthName] = [];
      }
      pastToCurrentModulesByMonth[monthName].push(module as Module);
      return;
    }

    const year = startDate.getFullYear();
    const month = startDate.getMonth();
    const key = `${year}-${month}`;

    if (!modulesByMonth[key]) {
      modulesByMonth[key] = {
        year,
        month,
        date: startDate,
        modules: [],
      };
    }

    modulesByMonth[key].modules.push(module as Module);
  });

  Object.keys(pastToCurrentModulesByMonth).forEach((monthName) => {
    addGroup(monthName, pastToCurrentModulesByMonth[monthName], true);
  });

  const currentMonthKey = `${currentYear}-${currentMonth}`;
  const currentMonthName = getMonthName(new Date(currentYear, currentMonth));

  if (modulesByMonth[currentMonthKey]) {
    addGroup(currentMonthName, modulesByMonth[currentMonthKey].modules, true);
  } else if (!addedGroupNames.has(currentMonthName)) {
    const emptyPlaceholder = {
      id: `empty-current-month-${Date.now()}`,
      isEmpty: true,
      message: `No content scheduled for ${currentMonthName}.`,
      status: "",
    } as unknown as Module;

    addGroup(currentMonthName, [emptyPlaceholder], true);
  }
  delete modulesByMonth[currentMonthKey];

  const futureMonths = Object.values(modulesByMonth)
    .filter(
      (item) =>
        item.year > currentYear ||
        (item.year === currentYear && item.month > currentMonth),
    )
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  futureMonths.forEach((item) => {
    const monthName = getMonthName(item.date);
    addGroup(monthName, item.modules, true);
    delete modulesByMonth[`${item.year}-${item.month}`];
  });

  const pastMonths = Object.values(modulesByMonth).sort(
    (a, b) => b.date.getTime() - a.date.getTime(),
  );

  pastMonths.forEach((item) => {
    const monthName = getMonthName(item.date);
    addGroup(monthName, item.modules, true);
  });

  groups.forEach((group) => {
    if (group.data.length === 1 && group.data[0].isEmpty) {
      group.count = undefined;
    } else {
      group.count = group.data.length;
    }
  });

  return groups;
};
